import { type Collection, createCollection, type UtilsRecord } from "@tanstack/db";
import type { FhirCollectionUtils } from "../fhir/base";
import type { FhirCollectionName, FhirCollectionRegistry } from "../fhir/types";
import type { CollectionConfig, CollectionEntry, DataStoreClientConfig } from "./types";

/** Registry-based data store client - no FHIR dependencies */
export class DataStoreClient {
  private registry = new Map<string, CollectionEntry>();
  private disposed = false;

  // Consumer tracking for lazy subscriptions
  private consumers = new Map<string, number>();
  private consumerListeners = new Set<() => void>();
  private consumerSnapshot = {};

  /** Register a consumer for a collection name. Returns idempotent cleanup fn. */
  registerConsumer(name: string): () => void {
    this.consumers.set(name, (this.consumers.get(name) ?? 0) + 1);
    this.notifyConsumerListeners();

    let cleaned = false;
    return () => {
      if (cleaned) return;
      cleaned = true;
      const count = this.consumers.get(name) ?? 0;
      if (count <= 1) {
        this.consumers.delete(name);
      } else {
        this.consumers.set(name, count - 1);
      }
      this.notifyConsumerListeners();
    };
  }

  /** Check if any component is consuming a collection */
  hasConsumers(name: string): boolean {
    return (this.consumers.get(name) ?? 0) > 0;
  }

  /** Subscribe to consumer changes (for useSyncExternalStore) */
  subscribeConsumers(listener: () => void): () => void {
    this.consumerListeners.add(listener);
    return () => this.consumerListeners.delete(listener);
  }

  /** Get immutable snapshot ref (for useSyncExternalStore) */
  getConsumerSnapshot(): object {
    return this.consumerSnapshot;
  }

  private notifyConsumerListeners(): void {
    this.consumerSnapshot = {};
    for (const listener of this.consumerListeners) {
      listener();
    }
  }

  constructor(config: DataStoreClientConfig = {}) {
    // Pre-register collections if provided
    if (config.collections) {
      for (const { name, config: collectionConfig } of config.collections) {
        this.registerCollection(name, collectionConfig);
      }
    }
  }

  /** Type-safe overload for known FHIR collection names */
  registerCollection<K extends FhirCollectionName>(
    name: K,
    config: CollectionConfig<FhirCollectionRegistry[K], string, FhirCollectionUtils<FhirCollectionRegistry[K]>>,
  ): Collection<FhirCollectionRegistry[K], string, FhirCollectionUtils<FhirCollectionRegistry[K]>>;

  /** Generic fallback for custom collections */
  registerCollection<T extends object, K extends string = string, U extends UtilsRecord = UtilsRecord>(
    name: string,
    config: CollectionConfig<T, K, U>,
  ): Collection<T, K, U>;

  /** Register a collection with the client */
  // biome-ignore lint/suspicious/noExplicitAny: implementation signature must be wider than overloads
  registerCollection(name: string, config: CollectionConfig<any, any, any>): any {
    if (this.registry.has(name)) {
      throw new Error(`Collection "${name}" already registered`);
    }

    // Type assertions needed for registry storage with generic types
    // biome-ignore lint/suspicious/noExplicitAny: type erasure for heterogeneous registry
    const collection = createCollection(config as any);
    this.registry.set(name, {
      // biome-ignore lint/suspicious/noExplicitAny: type erasure for heterogeneous registry
      config: config as any,
      // biome-ignore lint/suspicious/noExplicitAny: type erasure for heterogeneous registry
      collection: collection as any,
    });

    return collection;
  }

  /** Type-safe overload for known FHIR collection names */
  getCollection<K extends FhirCollectionName>(
    name: K,
  ): Collection<FhirCollectionRegistry[K], string, FhirCollectionUtils<FhirCollectionRegistry[K]>>;

  /** Generic fallback for custom collections */
  getCollection<T extends object, K extends string = string, U extends UtilsRecord = UtilsRecord>(
    name: string,
  ): Collection<T, K, U>;

  /** Get a registered collection by name */
  getCollection(name: string) {
    const entry = this.registry.get(name);
    if (!entry) {
      throw new Error(`Collection "${name}" not found. Did you register it?`);
    }
    return entry.collection;
  }

  /** Check if a collection is registered */
  hasCollection(name: string): boolean {
    return this.registry.has(name);
  }

  /** Get all registered collection names */
  getCollectionNames(): string[] {
    return Array.from(this.registry.keys());
  }

  /** Preload all registered collections */
  async preload(): Promise<void> {
    if (this.disposed) return;

    const preloadPromises = Array.from(this.registry.values()).map((entry) => entry.collection.preload());

    await Promise.all(preloadPromises);
  }

  /** Dispose client and cleanup all collections */
  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;

    const cleanupPromises = Array.from(this.registry.values()).map((entry) => entry.collection.cleanup());

    await Promise.all(cleanupPromises);
    this.registry.clear();
    this.consumers.clear();
    this.notifyConsumerListeners();
  }
}
